import { useEffect, useMemo, useRef } from "react";

import { UseFormReturn, useWatch } from "react-hook-form";

import useGetTaskTypeTemplate from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTypeTemplate";
import { TemplateInfo } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import { FormData } from "../../core/types";
import {
  extractTaskTypeId,
  performTaskTemplateRollover,
} from "../../utils/fieldRollover";
import { TemplateTransition } from "../types";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

interface UseTemplateSwitchingProps {
  formMethods: UseFormReturn<FormData>;
  currentTemplate: TemplateInfo | null;
  setCurrentTemplate: (template: TemplateInfo | null) => void;
  setTemplateTransition: (transition: TemplateTransition) => void;
  setLastRolledOverFields: (fields: Record<string, unknown>) => void;
  setLastDiscardedFields: (fields: string[]) => void;
}

interface UseTemplateSwitchingReturn {
  isLoadingSwitchingTemplate: boolean;
  switchingTemplateError: boolean;
}

export const useTemplateSwitching = ({
  formMethods,
  currentTemplate,
  setCurrentTemplate,
  setTemplateTransition,
  setLastRolledOverFields,
  setLastDiscardedFields,
}: UseTemplateSwitchingProps): UseTemplateSwitchingReturn => {
  const auth = useAdvisorRequestAuth();
  const taskTypeFieldKey = "taskType" as const;

  // Track previous task type ID to prevent unnecessary template switching
  const previousTaskTypeIdRef = useRef<string | null>(null);

  // Track if this is initial data loading to prevent template switching during initial reset
  const isInitialLoadingRef = useRef<boolean>(true);

  // Watch taskType field changes using react-hook-form's watch
  const taskTypeValue = useWatch({
    control: formMethods.control,
    name: taskTypeFieldKey,
  });

  // Extract task type ID and determine if it has actually changed
  const taskTypeChangeInfo = useMemo(() => {
    const extractedId = extractTaskTypeId(taskTypeValue);
    const previousId = previousTaskTypeIdRef.current;
    const isInitialLoading = isInitialLoadingRef.current;

    // Consider it changed if:
    // 1. We have a valid extracted ID AND
    // 2. This is NOT initial loading AND
    // 3. The ID is different from previous
    const hasChanged =
      extractedId !== null && !isInitialLoading && extractedId !== previousId;

    // Handle initial loading: if we have an ID for the first time, it's initial load, not a change
    if (extractedId !== null && isInitialLoading) {
      isInitialLoadingRef.current = false; // Mark initial loading as complete
      previousTaskTypeIdRef.current = extractedId; // Set the baseline
    } else if (hasChanged) {
      previousTaskTypeIdRef.current = extractedId; // Update for next comparison
    }

    return {
      currentId: extractedId,
      previousId,
      hasChanged,
      isInitialLoad: isInitialLoading,
    } as const;
  }, [taskTypeValue]);

  // Fetch template whenever we have a valid extracted task type ID
  const shouldFetchTemplate =
    taskTypeChangeInfo.currentId !== null &&
    (taskTypeChangeInfo.hasChanged ? true : taskTypeChangeInfo.isInitialLoad);

  // Fetch template by task type only when taskType has actually changed
  const {
    data: switchingTemplateData,
    isLoading: isLoadingSwitchingTemplate,
    hasError: switchingTemplateError,
  } = useGetTaskTypeTemplate(
    shouldFetchTemplate ? taskTypeChangeInfo.currentId : null,
    auth
  );

  // Handle template switching when new template data arrives
  useEffect(() => {
    // Skip template switching if task type hasn't actually changed
    if (!taskTypeChangeInfo.hasChanged) {
      return;
    }

    if (!switchingTemplateData || !currentTemplate) {
      return;
    }

    // Parse new template
    const parsedTemplate = TemplateInfo.safeParse(switchingTemplateData);
    if (!parsedTemplate.success) {
      console.error(
        "[useTemplateSwitching] Failed to parse task type template:",
        parsedTemplate.error
      );
      return;
    }

    const newTemplate = parsedTemplate.data;
    const oldTemplate = currentTemplate;

    // If template hasn't actually changed, do nothing
    if (oldTemplate.templateId === newTemplate.templateId) {
      return;
    }

    // Start template transition
    setTemplateTransition({
      oldTemplate,
      newTemplate,
      isTransitioning: true,
    });

    // Perform field rollover using current form values (not initial values)
    const currentFormValues = formMethods.getValues();

    const rolloverResult = performTaskTemplateRollover(
      currentFormValues,
      oldTemplate,
      newTemplate,
      taskTypeFieldKey
    );

    // Clear discarded fields from form
    rolloverResult.discarded.forEach((fieldKey) => {
      formMethods.setValue(fieldKey, undefined, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
      });
    });

    // Update/preserve rolled over fields in form
    Object.entries(rolloverResult.rolledOver).forEach(
      ([fieldKey, fieldValue]) => {
        // Skip taskType field - let it keep its current value naturally
        if (fieldKey === taskTypeFieldKey) {
          return;
        }

        formMethods.setValue(fieldKey, fieldValue, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
      }
    );

    // Update template and rollover tracking
    setCurrentTemplate(newTemplate);
    setLastRolledOverFields(rolloverResult.rolledOver);
    setLastDiscardedFields(rolloverResult.discarded);

    // End transition
    setTemplateTransition({
      oldTemplate,
      newTemplate,
      isTransitioning: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    switchingTemplateData,
    currentTemplate,
    setCurrentTemplate,
    setTemplateTransition,
    setLastRolledOverFields,
    setLastDiscardedFields,
    taskTypeChangeInfo.hasChanged,
  ]);

  return {
    isLoadingSwitchingTemplate: shouldFetchTemplate
      ? isLoadingSwitchingTemplate
      : false,
    switchingTemplateError,
  };
};
